import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { PWAProvider } from './contexts/PWAContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminProtectedRoute } from './components/AdminProtectedRoute';
import { NewLayout } from './components/NewLayout';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { NewDashboardPage } from './pages/NewDashboardPage';
import { TeamsPage } from './pages/TeamsPage';
import { TeamDetailPage } from './pages/TeamDetailPage';
import { NewPlayersPage } from './pages/NewPlayersPage';
import { CreateTeamWizardPage } from './pages/CreateTeamWizardPage';
import { EditTeamSettingsPage } from './pages/EditTeamSettingsPage';
import { PlayerProfilePage } from './pages/PlayerProfilePage';
import { AddCoachPage } from './pages/AddCoachPage';
import { AssessPlayersPage } from './pages/AssessPlayersPage';
import { AssessPlayerPage } from './pages/AssessPlayerPage';
import { DevelopmentCyclePage } from './pages/DevelopmentCyclePage';
import { ClubDashboardPage } from './pages/ClubDashboardPage';
import { TeamPage } from './pages/TeamPage';

import { AssessmentPage } from './pages/AssessmentPage';
import { ReportsPage } from './pages/ReportsPage';
import { ProfilePage } from './pages/ProfilePage';
import { VerifyEmailChangePage } from './pages/VerifyEmailChangePage';
import { VerifyRegistrationPage } from './pages/VerifyRegistrationPage';
import { CompletePasswordChangePage } from './pages/CompletePasswordChangePage';
import { AcceptInvitationPage } from './pages/AcceptInvitationPage';
import { AdminLoginPage } from './pages/admin/AdminLoginPage';
import { AdminAcceptInvitationPage } from './pages/admin/AdminAcceptInvitationPage';
import AdminClubSetupPage from './pages/admin/AdminClubSetupPage';
import AdminSubscribePage from './pages/admin/AdminSubscribePage';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { AdminTeamsPage } from './pages/admin/AdminTeamsPage';
import { AdminContactsPage } from './pages/admin/AdminContactsPage';
import { AdminSettingsPage } from './pages/admin/AdminSettingsPage';
import { ClubAdminRegisterPage } from './pages/admin/ClubAdminRegisterPage';
import { SelectSubscriptionTierPage } from './pages/admin/SelectSubscriptionTierPage';
import { ConfirmSubscriptionPage } from './pages/admin/ConfirmSubscriptionPage';
import { PaymentSuccessPage } from './pages/admin/PaymentSuccessPage';
import { PaymentCancelPage } from './pages/admin/PaymentCancelPage';
import { VerifyEmailPage } from './pages/admin/VerifyEmailPage';
import { OfflineIndicator } from './components/OfflineIndicator';
import { CoachPaymentSuccessPage } from './pages/CoachPaymentSuccessPage';
import { CoachPaymentCancelPage } from './pages/CoachPaymentCancelPage';
import { SubscriptionSuccessPage } from './pages/SubscriptionSuccessPage';
import { TasksPage } from './pages/TasksPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { OnboardingCompletePage } from './pages/OnboardingCompletePage';

// Redirect legacy player route to new route inside NewLayout
function PlayerProfileRedirect() {
  const { playerId } = useParams();
  return <Navigate to={`/players/${playerId}`} replace />;
}

function App() {
  return (
    <PWAProvider>
      <AuthProvider>
        <OfflineIndicator />
        <Router>
        <Routes>
          {/* Administration Portal Routes */}
          <Route path="/administration-club" element={<AdminLoginPage />} />
          <Route path="/administration-club/register" element={<ClubAdminRegisterPage />} />
          <Route path="/administration-club/select-tier" element={<SelectSubscriptionTierPage />} />
          <Route path="/administration-club/confirm-subscription" element={<ConfirmSubscriptionPage />} />
          <Route path="/administration-club/payment-success" element={<PaymentSuccessPage />} />
          <Route path="/administration-club/payment-cancel" element={<PaymentCancelPage />} />
          <Route path="/administration-club/verify-email" element={<VerifyEmailPage />} />
          <Route path="/administration-club/email-verified" element={<VerifyEmailPage />} />
          <Route path="/administration-club/accept-invitation" element={<AdminAcceptInvitationPage />} />
          <Route
            path="/administration-club/setup"
            element={
              <AdminProtectedRoute>
                <AdminClubSetupPage />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/administration-club/subscribe"
            element={
              <AdminProtectedRoute>
                <AdminSubscribePage />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/administration-club/dashboard"
            element={
              <AdminProtectedRoute>
                <AdminDashboardPage />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/administration-club/teams"
            element={
              <AdminProtectedRoute>
                <AdminTeamsPage />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/administration-club/contacts"
            element={
              <AdminProtectedRoute>
                <AdminContactsPage />
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/administration-club/settings"
            element={
              <AdminProtectedRoute>
                <AdminSettingsPage />
              </AdminProtectedRoute>
            }
          />

          {/* Main Application Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/payment-success" element={<CoachPaymentSuccessPage />} />
          <Route path="/payment-cancel" element={<CoachPaymentCancelPage />} />
          <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/verify-email-change" element={<VerifyEmailChangePage />} />
          <Route path="/verify-registration" element={<VerifyRegistrationPage />} />
          <Route path="/verify-password-change" element={<CompletePasswordChangePage />} />
          <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          {/* Onboarding (protected but outside NewLayout) */}
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
          <Route path="/onboarding/complete" element={<ProtectedRoute><OnboardingCompletePage /></ProtectedRoute>} />
          <Route path="/onboarding/assess/:playerId" element={<ProtectedRoute><AssessPlayerPage /></ProtectedRoute>} />
          
          {/* Protected Routes with New Layout */}
          <Route element={<ProtectedRoute><NewLayout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<NewDashboardPage />} />
            <Route path="/teams" element={<TeamsPage />} />
            <Route path="/team/create" element={<CreateTeamWizardPage />} />
            <Route path="/teams/:id" element={<TeamDetailPage />} />
            <Route path="/teams/:id/edit" element={<EditTeamSettingsPage />} />
            <Route path="/teams/:id/add-coach" element={<AddCoachPage />} />
            <Route path="/add-coach" element={<AddCoachPage />} />
            <Route path="/players" element={<NewPlayersPage />} />
            <Route path="/players/:playerId" element={<PlayerProfilePage />} />
            <Route path="/assess" element={<AssessPlayersPage />} />
            <Route path="/assess/:playerId" element={<AssessPlayerPage />} />
            <Route path="/develop" element={<DevelopmentCyclePage />} />
            <Route path="/club" element={<ClubDashboardPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/reports/team/:teamId" element={<ReportsPage />} />
            <Route path="/reports/player/:playerId" element={<ReportsPage />} />
            <Route path="/reports/team/:teamId/player/:playerId" element={<ReportsPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/profile" element={<ProfilePage />} />
          </Route>

          {/* Legacy Routes (keep for backward compatibility) */}
          <Route
            path="/team/:teamId"
            element={
              <ProtectedRoute>
                <TeamPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team/:teamId/player/:playerId"
            element={
              <ProtectedRoute>
                <PlayerProfileRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team/:teamId/assessment/:playerId"
            element={
              <ProtectedRoute>
                <AssessmentPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Router>
      </AuthProvider>
    </PWAProvider>
  );
}

export default App;
